// BUILD_TEST_2025_DEC_03_UNIQUE
import { useState } from 'react';
import { Download, FileText, Calendar, DollarSign, TrendingUp, Filter } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getTodayIST } from '../utils/dateUtils';
import { buildAnalyticsCsv, downloadCsv } from '../utils/analytics';

const Reports = () => {
  // Get date 30 days ago in IST
  const get30DaysAgoIST = () => {
    const now = new Date();

    // Get IST date parts using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    const parts = formatter.formatToParts(now);
    const year = parseInt(parts.find(p => p.type === 'year').value);
    const month = parseInt(parts.find(p => p.type === 'month').value);
    const day = parseInt(parts.find(p => p.type === 'day').value);

    // Create date and subtract 30 days
    const istDate = new Date(year, month - 1, day);
    istDate.setDate(istDate.getDate() - 30);

    const newYear = istDate.getFullYear();
    const newMonth = String(istDate.getMonth() + 1).padStart(2, '0');
    const newDay = String(istDate.getDate()).padStart(2, '0');

    return `${newYear}-${newMonth}-${newDay}`;
  };

  const [filters, setFilters] = useState({
    reportType: 'transactions',
    dateFrom: get30DaysAgoIST(),
    dateTo: getTodayIST(),
    status: '',
    format: 'csv'
  });
  const [loading, setLoading] = useState(false);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const downloadReport = async () => {
    try {
      setLoading(true);

      // Daily summary comes from the analytics endpoint (one row per day with commission)
      if (filters.reportType === 'summary') {
        const response = await apiService.getAnalytics({
          mode: 'daily',
          from_date: filters.dateFrom,
          to_date: filters.dateTo
        });
        if (!response.success) {
          toast.error('Failed to generate report');
          return;
        }
        const baseName = `daily-summary-${filters.dateFrom}-to-${filters.dateTo}`;
        if (filters.format === 'csv') {
          downloadCsv(buildAnalyticsCsv(response.data), `${baseName}.csv`);
        } else {
          const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseName}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
        toast.success('Report downloaded successfully');
        return;
      }

      // Map reportType to backend type parameter
      const typeMapping = {
        'transactions': 'both',
        'payin': 'payin',
        'payout': 'payout'
      };

      const params = {
        from_date: filters.dateFrom,
        to_date: filters.dateTo,
        status: filters.status,
        format: filters.format,
        type: typeMapping[filters.reportType] || 'both'
      };

      const response = await apiService.getTransactionReport(params);

      // Handle different response types based on format
      if (filters.format === 'csv') {
        // For CSV, the response contains multiple files
        if (response.success && response.data.files && response.data.files.length > 0) {
          // Download each CSV file separately
          response.data.files.forEach((file, index) => {
            const blob = new Blob([file.content], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.filename;
            document.body.appendChild(a);

            // Add slight delay between downloads to ensure browser handles them properly
            setTimeout(() => {
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }, index * 100);
          });

          toast.success(`${response.data.files.length} report file(s) downloaded successfully`);
        } else {
          toast.error('No data available for the selected criteria');
        }
      } else {
        // For JSON format
        if (response.success) {
          const jsonData = JSON.stringify(response.data, null, 2);
          const blob = new Blob([jsonData], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${filters.reportType}-report-${filters.dateFrom}-to-${filters.dateTo}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          toast.success('Report downloaded successfully');
        } else {
          toast.error('Failed to generate report');
        }
      }

    } catch (error) {
      console.error('Report download failed:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const quickReports = [
    {
      title: 'Today\'s Transactions',
      description: 'Download all transactions from today',
      icon: Calendar,
      color: 'blue',
      action: () => {
        const today = getTodayIST();
        setFilters(prev => ({
          ...prev,
          dateFrom: today,
          dateTo: today
        }));
      }
    },
    {
      title: 'Last 7 Days',
      description: 'Download transactions from past week',
      icon: TrendingUp,
      color: 'green',
      action: () => {
        const now = new Date();

        // Get IST date parts using Intl.DateTimeFormat
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });

        const parts = formatter.formatToParts(now);
        const year = parseInt(parts.find(p => p.type === 'year').value);
        const month = parseInt(parts.find(p => p.type === 'month').value);
        const day = parseInt(parts.find(p => p.type === 'day').value);

        // Create date and subtract 7 days
        const istDate = new Date(year, month - 1, day);
        istDate.setDate(istDate.getDate() - 7);

        const weekYear = istDate.getFullYear();
        const weekMonth = String(istDate.getMonth() + 1).padStart(2, '0');
        const weekDay = String(istDate.getDate()).padStart(2, '0');

        setFilters(prev => ({
          ...prev,
          dateFrom: `${weekYear}-${weekMonth}-${weekDay}`,
          dateTo: getTodayIST()
        }));
      }
    },
    {
      title: 'Last 30 Days',
      description: 'Download transactions from past month',
      icon: DollarSign,
      color: 'purple',
      action: () => {
        setFilters(prev => ({
          ...prev,
          dateFrom: get30DaysAgoIST(),
          dateTo: getTodayIST()
        }));
      }
    },
    {
      title: 'Custom Range',
      description: 'Select custom date range',
      icon: Filter,
      color: 'indigo',
      action: () => {
        // Just scroll to filters section
        document.getElementById('filters-section')?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="mt-1 text-sm text-gray-600">
          Download transaction reports and export data
        </p>
      </div>

      {/* Quick Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickReports.map((report, index) => (
          <button
            key={index}
            onClick={report.action}
            className="card p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 bg-${report.color}-100 rounded-lg`}>
                <report.icon className={`h-6 w-6 text-${report.color}-600`} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {report.title}
            </h3>
            <p className="text-sm text-gray-600">
              {report.description}
            </p>
          </button>
        ))}
      </div>

      {/* Custom Report Filters */}
      <div id="filters-section" className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Custom Report</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Type
            </label>
            <select
              value={filters.reportType}
              onChange={(e) => handleFilterChange('reportType', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="transactions">All Transactions</option>
              <option value="payin">Payin Only</option>
              <option value="payout">Payout Only</option>
              <option value="summary">Daily Summary (with commission)</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              disabled={filters.reportType === 'summary'}
              className="w-full disabled:bg-gray-100 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Export Format
            </label>
            <select
              value={filters.format}
              onChange={(e) => handleFilterChange('format', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="csv">CSV (Excel Compatible)</option>
              <option value="json">JSON</option>
            </select>
          </div>

          {/* Download Button */}
          <div className="flex items-end">
            <button
              onClick={downloadReport}
              disabled={loading}
              className="btn-primary w-full inline-flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Download size={16} className="mr-2" />
                  Download Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Report Information */}
      <div className="card p-6 bg-gray-50">
        <div className="flex items-start">
          <FileText className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Report Information</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Reports include all transaction details with customer information</li>
              <li>• CSV format can be opened directly in Excel or Google Sheets</li>
              <li>• JSON format is suitable for programmatic processing</li>
              <li>• Maximum date range: 90 days per report</li>
              <li>• Reports are generated in real-time with latest data</li>
              <li>• All amounts are in INR (Indian Rupees)</li>
              <li>• Daily Summary gives one row per IST day with payin amount, commission, net amount and payouts</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recent Reports (Mock Data) */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Recent Downloads
          </h3>
        </div>
        <div className="p-6">
          <div className="text-center text-gray-500 py-8">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No recent downloads</p>
            <p className="text-xs mt-1">Your downloaded reports will appear here</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
